// Ported from DealerFlow 360 (dealerflow360/web/src/shared/payments/CardFields.tsx) for LOS card down payments (A5):
// the same fields, checks and Accept.js handling, with Kendo 10's Input and Kendo's Error for the messages.
import { useEffect, useId, useImperativeHandle, useLayoutEffect, useMemo, useRef, useState, type Ref } from 'react';
import { Input, type InputChangeEvent } from '@progress/kendo-react-inputs';
import { Error as FieldMessage } from '@progress/kendo-react-labels';
import { dispatchCard, loadAcceptJs, type AcceptAuthData, type AcceptOpaqueData } from './acceptJs';
import {
  cardFieldErrors,
  cvvLength,
  formatCardNumber,
  formatExpiration,
  toAcceptCardData,
  type CardBilling,
  type CardField,
  type CardFieldValues,
} from './cardValidation';
import { useFieldErrors, type FieldErrorProps } from './fieldErrors';
import styles from './CardFields.module.scss';

export interface CardFieldsHandle {
  /** Shows every field's message; true when the card can be sent. */
  validate: () => boolean;
  /** Moves focus to the first field with a problem; false when there is none. */
  focusFirstIssue: () => boolean;
  /** Moves focus to the card number. */
  focus: () => void;
  /**
   * Sends the card to Authorize.Net and returns the token. The card number and CVV are emptied
   * as soon as Accept.js answers, whatever it answers.
   */
  tokenize: (auth: AcceptAuthData) => Promise<AcceptOpaqueData>;
  /** Empties the card number, expiration and CVV (after a decline). */
  clearCard: () => void;
}

interface CardFieldsProps {
  billing: CardBilling;
  onBillingChange: (patch: Partial<CardBilling>) => void;
  disabled?: boolean;
  /** What the card still needs, one message per field in the form's order; empty once it can be sent. */
  onIssuesChange?: (issues: string[]) => void;
  ref?: Ref<CardFieldsHandle>;
}

interface FieldSpec {
  field: CardField;
  label: string;
  inputMode?: 'numeric' | 'text';
  placeholder?: string;
  maxLength?: number;
  /** The field's width class: each field is as wide as what it holds. */
  size: 'number' | 'expiration' | 'cvv' | 'line' | 'city' | 'state' | 'zip';
}

const FIELDS: Record<CardField, FieldSpec> = {
  cardNumber: { field: 'cardNumber', label: 'Card number', inputMode: 'numeric', maxLength: 23, size: 'number' },
  expiration: { field: 'expiration', label: 'Expiration', inputMode: 'numeric', placeholder: 'MM/YY', maxLength: 7, size: 'expiration' },
  cvv: { field: 'cvv', label: 'CVV', inputMode: 'numeric', size: 'cvv' },
  nameOnCard: { field: 'nameOnCard', label: 'Name on card', size: 'line' },
  street: { field: 'street', label: 'Billing street', size: 'line' },
  city: { field: 'city', label: 'City', size: 'city' },
  state: { field: 'state', label: 'State', maxLength: 2, size: 'state' },
  zip: { field: 'zip', label: 'ZIP', inputMode: 'numeric', maxLength: 10, size: 'zip' },
};

/**
 * The rows, in order. A row's first field is labelled in the label column, any other beside it, and a pair's
 * messages go under the pair, so a message never pushes the field beside it sideways.
 */
const ROWS: CardField[][] = [['cardNumber'], ['expiration', 'cvv'], ['nameOnCard'], ['street'], ['city'], ['state', 'zip']];
const ORDER: CardField[] = ROWS.flat();
/** After the card's own rows comes the billing address. */
const BILLING_START = 2;

const EMPTY_CARD = { cardNumber: '', expiration: '', cvv: '' };
/** The fields shown formatted, whose caret is put back where it was after the reformat. */
type FormattedField = 'cardNumber' | 'expiration';

/** The typed value as the field shows it: the card number grouped for its brand, the expiration as MM/YY. */
function shown(field: CardField, value: string): string {
  if (field === 'cardNumber') return formatCardNumber(value);
  if (field === 'expiration') return formatExpiration(value);
  return value;
}

const digitsBefore = (value: string, position: number) => value.slice(0, position).replace(/\D/g, '').length;

/** The position just after the `digits`th digit of `value`, so the caret keeps its place among the digits. */
function positionAfterDigits(value: string, digits: number): number {
  let seen = 0;
  for (let position = 0; position < value.length; position += 1) {
    if (seen === digits) return position;
    if (/\d/.test(value[position]!)) seen += 1;
  }
  return value.length;
}

/** One field's message under its control; nothing while the field is valid or untouched. */
function Message({ id, message }: FieldErrorProps) {
  if (!message) return null;
  return <FieldMessage id={id}>{message}</FieldMessage>;
}

/**
 * New card entry for a card down payment: Kendo inputs, with the card number, expiration and CVV kept in this
 * component's state only. Nothing here sends the card anywhere but Authorize.Net, through Accept.js.
 */
export function CardFields({ billing, onBillingChange, disabled = false, onIssuesChange, ref }: CardFieldsProps) {
  const [card, setCard] = useState(EMPTY_CARD);
  const values: CardFieldValues = { ...card, ...billing };
  const errors = useMemo(() => cardFieldErrors({ ...card, ...billing }), [card, billing]);
  const fieldErrors = useFieldErrors<CardFieldValues>(errors);
  const issueKey = ORDER.map((field) => errors[field] ?? '').join('\n');
  const baseId = useId();
  const idOf = (field: CardField) => `${baseId}${field}`;
  // Where the caret was, counted in digits, while a formatted field is typed in.
  const caret = useRef<{ field: FormattedField; digits: number } | null>(null);

  useEffect(() => {
    onIssuesChange?.(issueKey.split('\n').filter(Boolean));
  }, [issueKey, onIssuesChange]);

  // Reformatting a value moves the caret to its end; put it back after the same number of digits.
  useLayoutEffect(() => {
    const pending = caret.current;
    if (!pending) return;
    caret.current = null;
    const input = document.getElementById(`${baseId}${pending.field}`) as HTMLInputElement | null;
    if (!input || document.activeElement !== input) return;
    const position = positionAfterDigits(input.value, pending.digits);
    input.setSelectionRange(position, position);
  }, [card, baseId]);

  const clearSecrets = (fields: (keyof typeof EMPTY_CARD)[]) =>
    setCard((current) => ({ ...current, ...Object.fromEntries(fields.map((field) => [field, ''])) }));

  useImperativeHandle(
    ref,
    () => ({
      validate: () => fieldErrors.revealErrors(),
      focusFirstIssue: () => {
        const first = ORDER.find((field) => errors[field]);
        if (!first) return false;
        document.getElementById(`${baseId}${first}`)?.focus();
        return true;
      },
      focus: () => document.getElementById(`${baseId}cardNumber`)?.focus(),
      tokenize: async (auth) => {
        const cardData = toAcceptCardData({ ...card, ...billing });
        await loadAcceptJs(auth.scriptUrl);
        try {
          return await dispatchCard(auth, cardData);
        } finally {
          // The emptied fields say nothing until someone types in them again.
          clearSecrets(['cardNumber', 'cvv']);
          fieldErrors.resetTouched();
        }
      },
      clearCard: () => {
        clearSecrets(['cardNumber', 'expiration', 'cvv']);
        fieldErrors.resetTouched();
      },
    }),
    [card, billing, errors, fieldErrors, baseId],
  );

  const change = (field: CardField, event: InputChangeEvent) => {
    const value = String(event.value ?? '');
    if (field === 'cardNumber' || field === 'expiration') {
      const input = event.syntheticEvent?.target as HTMLInputElement | undefined;
      const position = input?.selectionStart;
      caret.current = typeof position === 'number' ? { field, digits: digitsBefore(value, position) } : null;
    }
    if (field in EMPTY_CARD) setCard((current) => ({ ...current, [field]: shown(field, value) }));
    else onBillingChange({ [field]: value } as Partial<CardBilling>);
    fieldErrors.markTouched([field]);
  };

  const input = ({ field, inputMode, placeholder, maxLength, size }: FieldSpec, withMessage: boolean) => (
    <div className={`${styles.control} ${styles[size]}`}>
      <Input
        id={idOf(field)}
        name={field}
        value={values[field]}
        disabled={disabled}
        // Staff type customers' cards: the browser neither offers to save them nor fills in another's.
        autoComplete="off"
        inputMode={inputMode}
        placeholder={placeholder}
        // An American Express card has a 4-digit CVV; any other, 3 digits.
        maxLength={field === 'cvv' ? cvvLength(card.cardNumber) : maxLength}
        spellCheck={false}
        onChange={(event) => change(field, event)}
        onBlur={() => fieldErrors.markTouched([field])}
        {...fieldErrors.controlProps(field)}
      />
      {withMessage && <Message {...fieldErrors.errorProps(field)} />}
    </div>
  );

  return (
    <div className={styles.form}>
      {ROWS.map((row, index) => {
        const specs = row.map((field) => FIELDS[field]);
        const [first, ...beside] = specs;
        const pair = beside.length > 0;
        return (
          <div key={first!.field} className={`${styles.row} ${index === BILLING_START ? styles.billingStart : ''}`}>
            <label htmlFor={idOf(first!.field)}>{first!.label}</label>
            <div className={styles.cell}>
              <div className={styles.fields}>
                {input(first!, !pair)}
                {beside.map((spec) => (
                  <div key={spec.field} className={styles.pair}>
                    <label htmlFor={idOf(spec.field)}>{spec.label}</label>
                    {input(spec, false)}
                  </div>
                ))}
              </div>
              {pair && specs.map((spec) => <Message key={spec.field} {...fieldErrors.errorProps(spec.field)} />)}
            </div>
          </div>
        );
      })}
    </div>
  );
}
